import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, extractError } from "@/lib/api";
import { toast } from "sonner";import { Loader2, Check, CreditCard, X, ExternalLink } from "lucide-react";

function PromoRedeemInline({ onDone }) {
  const [code, setCode] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/promo-codes/redeem", { code });
      toast.success(`Redeemed — ${data.months_free} month(s) free!`);
      setCode(""); onDone?.();
    } catch (err) { toast.error(extractError(err)); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="mt-3 flex gap-2" data-testid="promo-redeem-form">
      <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
        placeholder="ENTER CODE"
        data-testid="promo-redeem-input"
        className="flex-1 rounded-lg border border-stone2-200 bg-white px-4 py-2 text-sm font-mono tracking-widest" />
      <button type="submit" disabled={busy} data-testid="promo-redeem-submit"
        className="btn-primary text-sm">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Redeem
      </button>
    </form>
  );
}

const PLAN_LABELS = {
  family_free: "Free Explorer",
  family_plus: "Family Plus",
  family_premium: "Family Premium",
  sitter_free: "Sitter Free",
  sitter_enhanced: "Sitter Enhanced",
};

export default function SubscriptionSettings() {
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [params] = useSearchParams();

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/subscriptions/me"); setSub(data); }
    catch (e) { toast.error(extractError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (params.get("upgraded")) toast.success("Subscription activated — welcome to the club!");
  }, [params]);

  const cancel = async () => {
    if (!window.confirm("Cancel your subscription at the end of the current period?")) return;
    setCancelling(true);
    try { await api.post("/subscriptions/cancel"); toast.success("Cancellation scheduled"); load(); }
    catch (e) { toast.error(extractError(e)); }
    finally { setCancelling(false); }
  };

  return (
    <div className="min-h-screen bg-stone2-50">
      <Navbar />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
        <span className="section-eyebrow">Membership</span>
        <h1 className="mt-2 text-3xl font-heading font-bold text-sage-900 flex items-center gap-2">
          <CreditCard className="w-6 h-6 text-sage-600" /> Subscription
        </h1>

        {loading && <div className="mt-6 text-stone2-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…</div>}

        {sub && (
          <section className="paper-card p-6 mt-6" data-testid="subscription-card">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="text-xs uppercase tracking-[0.18em] font-bold text-sage-700">Current plan</div>
                <div className="text-2xl font-heading font-bold text-sage-900 mt-1">{PLAN_LABELS[sub.plan] || sub.plan}</div>
                <div className="text-xs text-stone2-500 mt-1">
                  Status <span className="font-semibold">{sub.status || "active"}</span>
                  {sub.period ? ` · billed ${sub.period}ly` : ""}
                  {sub.current_period_end ? ` · renews ${new Date(sub.current_period_end).toLocaleDateString()}` : ""}
                </div>
              </div>
              <Link to="/pricing" className="btn-primary text-sm" data-testid="subscription-change-plan">
                Change plan
              </Link>
            </div>

            {sub.cancel_at_period_end && (
              <div className="mt-4 rounded-lg bg-ochre-50 border border-ochre-200 p-3 text-sm text-ochre-900" data-testid="subscription-cancel-notice">
                Your subscription will cancel at the end of the current period.
              </div>
            )}

            {sub.stripe_subscription_id && !sub.cancel_at_period_end && (
              <button type="button" onClick={cancel} disabled={cancelling}
                data-testid="subscription-cancel-btn"
                className="mt-4 inline-flex items-center gap-2 rounded-full border border-danger text-danger px-4 py-2 text-sm font-semibold hover:bg-danger/5">
                {cancelling ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />} Cancel at period end
              </button>
            )}
          </section>
        )}

        <section className="paper-card p-6 mt-4">
          <h2 className="font-heading font-semibold text-sage-900">Have a promo code?</h2>
          <PromoRedeemInline onDone={load} />
        </section>

        <section className="paper-card p-6 mt-4">
          <h2 className="font-heading font-semibold text-sage-900">Included in your plan</h2>
          <ul className="mt-3 text-sm text-sage-900 space-y-1.5">
            {sub?.plan === "family_free" && [
              "Create profiles", "Browse & save 2 favorites", "3 free messages, then upgrade",
            ].map((f) => <li key={f} className="flex items-center gap-2"><Check className="w-4 h-4 text-sage-600" /> {f}</li>)}
            {sub?.plan === "family_plus" && [
              "Unlimited messaging & favorites", "AI compatibility matching",
              "Care journal", "Digital care binder", "Session summaries",
            ].map((f) => <li key={f} className="flex items-center gap-2"><Check className="w-4 h-4 text-sage-600" /> {f}</li>)}
            {sub?.plan === "family_premium" && [
              "Everything in Family Plus", "Priority booking",
              "Last-minute booking access", "Behavioral insights",
              "Dedicated account support",
            ].map((f) => <li key={f} className="flex items-center gap-2"><Check className="w-4 h-4 text-sage-600" /> {f}</li>)}
            {sub?.plan === "sitter_free" && [
              "Visible to families within 10 miles", "Standard search placement",
            ].map((f) => <li key={f} className="flex items-center gap-2"><Check className="w-4 h-4 text-sage-600" /> {f}</li>)}
            {sub?.plan === "sitter_enhanced" && [
              "Top of search results", "Unlimited reach", "Read family reviews",
              "Eligible for last-minute bookings",
            ].map((f) => <li key={f} className="flex items-center gap-2"><Check className="w-4 h-4 text-sage-600" /> {f}</li>)}
          </ul>
        </section>
      </div>
    </div>
  );
}
